/**
 * ครบทดลองงาน — คิวคนที่ต้องตัดสิน "ผ่านทดลองงาน / ต่อโปร / ไม่ผ่าน".
 *
 * ระบบเตือนอย่างเดียว ไม่ตัดสินแทน: ครบ 3 เดือนแล้วสถานะไม่เปลี่ยนเองจนกว่าจะมีคนกด
 * และกด "ผ่าน" ก่อนครบกำหนดได้ทุกเมื่อ (บริษัทบอกว่าใครผ่านเร็วกว่าจะปรับเอง).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useProbationList,
  useProbationHistory,
  useDecideProbation,
  useFixProbation,
  type ProbationQueueRow,
  type ProbationDecidedStillRow,
} from '../../hooks';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import { CheckCircle2, CalendarClock, XCircle, MinusCircle, History, AlertTriangle } from 'lucide-react';

type View = 'queue' | 'all' | 'history';

const ACTION_LABEL: Record<string, string> = {
  pass: 'ผ่านทดลองงาน',
  extend: 'ต่อโปร',
  fail: 'ไม่ผ่าน',
  waive: 'ยกเว้น (ไม่ต้องตัดสิน)',
};

function thaiDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('th-TH', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function DueBadge({ daysLeft }: { daysLeft: number }) {
  const cls =
    daysLeft < 0 ? 'bg-red-100 text-red-700'
    : daysLeft === 0 ? 'bg-amber-100 text-amber-700'
    : 'bg-blue-100 text-blue-700';
  const text =
    daysLeft < 0 ? `เลยกำหนด ${Math.abs(daysLeft)} วัน`
    : daysLeft === 0 ? 'ครบกำหนดวันนี้'
    : `อีก ${daysLeft} วัน`;
  return <span className={`badge whitespace-nowrap ${cls}`}>{text}</span>;
}

function ProbationCard({ row, onDone }: { row: ProbationQueueRow; onDone: () => void }) {
  const decide = useDecideProbation();
  const toast = useToast();
  const confirm = useConfirm();
  const [openExtend, setOpenExtend] = useState(false);
  const [newEnd, setNewEnd] = useState('');
  const [note, setNote] = useState('');

  async function run(action: 'pass' | 'fail' | 'waive') {
    const label = ACTION_LABEL[action];
    const ok = await confirm({
      title: `${label} — ${row.emp_name}?`,
      message:
        action === 'fail'
          ? 'บันทึกผลว่าไม่ผ่านทดลองงาน (ยังไม่เลิกจ้างให้ — ต้องไปทำเรื่องที่หน้า "ลาออก/เลิกจ้าง" ต่อ)'
          : action === 'waive'
            ? 'เอาคนนี้ออกจากคิวโดยไม่บันทึกผลผ่าน/ไม่ผ่าน'
            : 'บันทึกว่าผ่านทดลองงานเป็นพนักงานประจำ',
    });
    if (!ok) return;
    try {
      await decide.mutateAsync({ emp_code: row.emp_code, action });
      toast.success(`${row.emp_name} — ${label}`);
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function submitExtend() {
    if (!newEnd) { toast.error('เลือกวันครบกำหนดใหม่'); return; }
    if (!note.trim()) { toast.error('ใส่เหตุผลที่ต่อโปร'); return; }
    try {
      await decide.mutateAsync({ emp_code: row.emp_code, action: 'extend', new_end_date: newEnd, note: note.trim() });
      toast.success(`ต่อโปร ${row.emp_name} ถึง ${thaiDate(newEnd)}`);
      setOpenExtend(false);
      setNewEnd(''); setNote('');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <div className="card p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">
            {row.emp_name}
            <span className="text-gray-400 text-xs font-normal ml-1">· {row.emp_code}</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">
            {row.position || '—'}{row.department ? ` · ${row.department}` : ''}
          </div>
        </div>
        <DueBadge daysLeft={row.days_left} />
      </div>

      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
        <div>เริ่มงาน: <span className="text-gray-800">{thaiDate(row.start_date)}</span></div>
        <div>ครบทดลองงาน: <span className="text-gray-800">{thaiDate(row.probation_end_date)}</span></div>
      </div>
      {row.probation_extend_count > 0 && (
        <div className="text-xs text-amber-700 mt-1">ต่อโปรมาแล้ว {row.probation_extend_count} ครั้ง</div>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        <button className="btn-primary text-xs" onClick={() => run('pass')} disabled={decide.isPending}>
          <CheckCircle2 className="w-3.5 h-3.5" /> ผ่านทดลองงาน
        </button>
        <button className="btn-secondary text-xs" onClick={() => setOpenExtend(true)} disabled={decide.isPending}>
          <CalendarClock className="w-3.5 h-3.5" /> ต่อโปร
        </button>
        <button
          className="btn-secondary text-xs text-red-700 border-red-200 hover:bg-red-50"
          onClick={() => run('fail')}
          disabled={decide.isPending}
        >
          <XCircle className="w-3.5 h-3.5" /> ไม่ผ่าน
        </button>
        <button className="btn-secondary text-xs text-gray-600" onClick={() => run('waive')} disabled={decide.isPending}>
          <MinusCircle className="w-3.5 h-3.5" /> ยกเว้น
        </button>
      </div>

      <Modal open={openExtend} onClose={() => setOpenExtend(false)} title={`ต่อโปร — ${row.emp_name}`}>
        <div className="space-y-3">
          <div className="text-xs text-gray-600">
            ครบกำหนดเดิม {thaiDate(row.probation_end_date)} — เลือกวันครบกำหนดใหม่
          </div>
          <div>
            <label className="label">ครบกำหนดใหม่</label>
            <input type="date" className="input" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} />
          </div>
          <div>
            <label className="label">เหตุผลที่ต่อโปร</label>
            <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="เช่น ยังต้องดูเรื่องความสม่ำเสมอในการเข้างานอีก 1 เดือน" />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setOpenExtend(false)}>ยกเลิก</button>
            <button className="btn-primary flex-1" onClick={submitExtend} disabled={decide.isPending}>ยืนยันต่อโปร</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const DECIDED_LABEL: Record<string, string> = {
  passed: 'ผ่านทดลองงานแล้ว',
  waived: 'ยกเว้นทดลองงานแล้ว',
  failed: 'ไม่ผ่านทดลองงาน',
};

/**
 * fb#664 ป้ายในรายชื่อยังเป็น "ทดลองงาน" แต่เคยกดตัดสินไปแล้ว → ไม่อยู่ในคิว.
 * ให้ HR เลือกว่าจะเปลี่ยนเป็นพนักงานประจำ หรือเปิดทดลองงานใหม่ (กลับเข้าคิว).
 */
function DecidedStillRow({ row }: { row: ProbationDecidedStillRow }) {
  const fix = useFixProbation();
  const toast = useToast();
  const confirm = useConfirm();
  const [openReopen, setOpenReopen] = useState(false);
  const [newEnd, setNewEnd] = useState('');

  async function makeRegular() {
    const ok = await confirm({
      title: `เปลี่ยนเป็นพนักงานประจำ — ${row.emp_name}?`,
      message: `${DECIDED_LABEL[row.probation_status]}${row.probation_decided_at ? ` เมื่อ ${thaiDate(row.probation_decided_at)}` : ''} — เปลี่ยนสถานะในทะเบียนจาก "ทดลองงาน" เป็น "ปกติ"`,
    });
    if (!ok) return;
    try {
      await fix.mutateAsync({ emp_code: row.emp_code, action: 'make_regular' });
      toast.success(`${row.emp_name} — เป็นพนักงานประจำแล้ว`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function reopen() {
    try {
      await fix.mutateAsync({ emp_code: row.emp_code, action: 'reopen', new_end_date: newEnd || undefined });
      toast.success(`${row.emp_name} — กลับเข้าคิวทดลองงานแล้ว`);
      setOpenReopen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <div className="bg-white rounded border border-amber-200 p-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {row.emp_name}
            <span className="text-gray-400 text-xs font-normal ml-1">· {row.emp_code}</span>
          </div>
          <div className="text-xs text-gray-600">
            {DECIDED_LABEL[row.probation_status] ?? row.probation_status}
            {row.probation_decided_at ? ` เมื่อ ${thaiDate(row.probation_decided_at)}` : ''}
            {' · '}ครบทดลองงาน {thaiDate(row.probation_end_date)}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {row.probation_status !== 'failed' && (
          <button className="btn-primary text-xs" onClick={makeRegular} disabled={fix.isPending}>
            <CheckCircle2 className="w-3.5 h-3.5" /> เปลี่ยนเป็นพนักงานประจำ
          </button>
        )}
        <button className="btn-secondary text-xs" onClick={() => { setNewEnd(''); setOpenReopen(true); }} disabled={fix.isPending}>
          <CalendarClock className="w-3.5 h-3.5" /> เปิดทดลองงานใหม่
        </button>
      </div>

      <Modal open={openReopen} onClose={() => setOpenReopen(false)} title={`เปิดทดลองงานใหม่ — ${row.emp_name}`}>
        <div className="space-y-3">
          <div className="text-xs text-gray-600">
            ยกเลิกผล "{DECIDED_LABEL[row.probation_status]}" แล้วกลับเข้าคิวครบทดลองงาน ให้ตัดสินใหม่ด้วยปุ่ม ผ่าน / ต่อโปร / ไม่ผ่าน
          </div>
          <div>
            <label className="label">วันครบทดลองงาน (เว้นว่าง = ใช้วันเดิม {thaiDate(row.probation_end_date)})</label>
            <input type="date" className="input" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setOpenReopen(false)}>ยกเลิก</button>
            <button className="btn-primary flex-1" onClick={reopen} disabled={fix.isPending}>ยืนยันเปิดทดลองงานใหม่</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/** Content-only version used by the People hub. */
export function HRProbationContent() {
  const [view, setView] = useState<View>('queue');
  const { data, isLoading, refetch } = useProbationList(view === 'all' ? 'all' : 'queue');
  const history = useProbationHistory(view === 'history');

  const rows = data?.rows ?? [];
  const counts = data?.counts;
  const unknownStart = data?.unknown_start ?? [];
  const decidedStill = data?.decided_still_probation ?? [];
  const months = data?.settings.probation_months ?? 3;
  const noticeDays = data?.settings.probation_notice_days ?? 7;

  const tabs: Array<{ key: View; label: string }> = [
    { key: 'queue', label: `ต้องตัดสิน${counts ? ` (${counts.overdue + counts.due + counts.upcoming})` : ''}` },
    { key: 'all', label: `อยู่ระหว่างทดลองงาน${counts ? ` (${counts.total_pending})` : ''}` },
    { key: 'history', label: 'ประวัติการตัดสิน' },
  ];

  return (
    <>
      <p className="text-sm text-gray-600 mb-3">
        {months === 0
          ? 'บริษัทนี้ตั้งค่าไว้ว่าไม่ใช้ระบบทดลองงาน (ตั้งค่า → ระยะทดลองงาน)'
          : `ระยะทดลองงาน ${months} เดือน · เตือนล่วงหน้า ${noticeDays} วันก่อนครบกำหนด แล้วเตือนซ้ำทุกวันจันทร์ถ้ายังไม่ตัดสิน · กด "ผ่าน" ก่อนครบกำหนดได้ทุกเมื่อ`}
      </p>

      {unknownStart.length > 0 && (
        <div className="card border-amber-200 bg-amber-50 p-3 mb-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0 text-sm">
              <div className="font-medium text-amber-900">
                ยังไม่รู้วันเริ่มงาน {unknownStart.length} คน — ระบบคำนวณวันครบทดลองงานให้ไม่ได้
              </div>
              <div className="text-xs text-amber-800 mt-1">
                คนกลุ่มนี้ไม่อยู่ในคิวและ<b>ไม่ถูกแจ้งเตือน</b> จนกว่าจะเติมวันเริ่มงานให้ก่อน
                (เติมแล้วระบบตั้งวันครบให้เองทันที) ·{' '}
                <Link to="/hr/people#all" className="underline">ไปเติมที่รายชื่อพนักงาน</Link>
              </div>
              <div className="text-xs text-amber-700 mt-1 truncate">
                {unknownStart.slice(0, 6).map((u) => u.emp_name).join(' · ')}
                {unknownStart.length > 6 ? ` · และอีก ${unknownStart.length - 6} คน` : ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {decidedStill.length > 0 && (
        <div className="card border-amber-200 bg-amber-50 p-3 mb-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-medium text-amber-900">
                สถานะยังเป็น "ทดลองงาน" แต่ตัดสินทดลองงานไปแล้ว {decidedStill.length} คน
              </div>
              <div className="text-xs text-amber-800 mt-1">
                คนกลุ่มนี้ไม่อยู่ในคิวและไม่ถูกแจ้งเตือน เพราะเคยกด ผ่าน/ยกเว้น/ไม่ผ่าน ไปแล้ว
                — เลือกว่าจะเปลี่ยนเป็นพนักงานประจำ หรือเปิดทดลองงานใหม่ให้กลับเข้าคิว
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-2">
                {decidedStill.map((r) => <DecidedStillRow key={r.emp_code} row={r} />)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-3 border-b border-gray-200 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition ${
              view === t.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {view === 'history' ? (
        history.isLoading ? (
          <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>
        ) : (history.data?.history ?? []).length === 0 ? (
          <p className="py-8 text-center text-gray-500">ยังไม่มีการตัดสินทดลองงาน</p>
        ) : (
          <div className="space-y-2">
            {(history.data?.history ?? []).map((h, i) => (
              <div key={`${h.emp_code}-${h.created_at}-${i}`} className="card p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{h.emp_name}</div>
                    <div className="text-xs text-gray-500">
                      {ACTION_LABEL[h.action] ?? h.action}
                      {h.action === 'extend' && h.new_end_date ? ` ถึง ${thaiDate(h.new_end_date)}` : ''}
                    </div>
                    {h.note && <div className="text-xs text-gray-600 mt-1">“{h.note}”</div>}
                  </div>
                  <div className="text-xs text-gray-400 text-right shrink-0">
                    <div>{new Date(h.created_at).toLocaleDateString('th-TH')}</div>
                    <div>{h.decided_by_name ?? '—'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : isLoading ? (
        <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          <History className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          {view === 'queue' ? 'ยังไม่มีใครถึงกำหนดตัดสินทดลองงาน' : 'ไม่มีใครอยู่ระหว่างทดลองงาน'}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {rows.map((row) => (
            <ProbationCard key={row.emp_code} row={row} onDone={refetch} />
          ))}
        </div>
      )}
    </>
  );
}

/** Standalone page (deep link from LINE card / briefing). */
export function HRProbation() {
  return (
    <AdminLayout title="ครบทดลองงาน">
      <HRProbationContent />
    </AdminLayout>
  );
}
